import { config } from '@iheartjane/config';
import type { Facets } from '@iheartjane/dm-sdk';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { ZodType } from 'zod';

import type { FacetProps } from './client/facets';
import { fetchFacets } from './client/facets';

interface FacetQueryProps extends FacetProps {
  enabled?: boolean;
  schemaObject?: ZodType<Facets>;
}

const facetQueryOptions = (params: FacetQueryProps) =>
  queryOptions({
    queryFn: () => fetchAlgoliaFacets(params),
    queryKey: ['facets', JSON.stringify(params)],
    enabled: params.enabled,
  });

export function useFacets(params: FacetQueryProps) {
  return useQuery(facetQueryOptions(params));
}

export function useGoldFacets(params: FacetQueryProps) {
  const { data: facets, isPending } = useQuery(facetQueryOptions(params));

  let finalFacets = facets;
  if (finalFacets && !finalFacets['has_brand_discount']) {
    finalFacets = { has_brand_discount: { true: 0 } };
  }

  return { data: finalFacets, isPending };
}

async function fetchAlgoliaFacets({
  schemaObject,
  ...args
}: Omit<FacetQueryProps, 'enabled'>) {
  const facets = await fetchFacets(
    {
      algoliaEnv: config.algoliaEnv,
      apiKey: config.algoliaApiKey,
      appId: config.algoliaAppId,
      url: config.algoliaUrl,
    },
    args
  );

  return (
    (schemaObject ? await schemaObject.parseAsync(facets) : facets) ?? null
  );
}

const FacetSchema = z.record(z.string(), z.number());

export type Facet = z.infer<typeof FacetSchema>;

export const ProductFacetsSchema = z.object({
  activities: FacetSchema.optional(),
  aggregate_rating: FacetSchema.optional(),
  applicable_bulk_special_ids: FacetSchema.optional(),
  'applicable_bundle_special_ids.discount': FacetSchema.optional(),
  'applicable_bundle_special_ids.qualify': FacetSchema.optional(),
  applicable_cart_total_special_ids: FacetSchema.optional(),
  applicable_special_ids: FacetSchema.optional(),
  available_weights: FacetSchema.optional(),
  brand_subtype: FacetSchema.optional(),
  brand: FacetSchema.optional(),
  bucket_price: FacetSchema.optional(),
  category: FacetSchema.optional(),
  compound_names: FacetSchema.optional(),
  custom_menu_row_ids: FacetSchema.optional(),
  feelings: FacetSchema.optional(),
  has_brand_discount: FacetSchema.optional(),
  kind: FacetSchema.optional(),
  percent_cbd: FacetSchema.optional(),
  percent_thc: FacetSchema.optional(),
  percent_thca: FacetSchema.optional(),
  root_types: FacetSchema.optional(),
  store_id: FacetSchema.optional(),
});

export type ProductFacets = z.infer<typeof ProductFacetsSchema>;

export const GoldFacetsSchema = z.object({
  has_brand_discount: FacetSchema.optional(),
});
